/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Small, side-effect-free utilities for reading OHDS-related custom attributes
 * off the basket / delivery shipment. Extracted so the inline picker
 * (`hd-method-card-date-picker.tsx`) and the auto-populate hook
 * (`use-auto-populate-delivery-schedule.ts`) — plus any future surface that
 * needs to react to the same backend signals — read them through a single
 * source of truth.
 *
 * No React, no providers. Safe to import from anywhere (server or client).
 */

import type { ShopperBasketsV2 } from '@/scapi';

/** Strict `yyyy-MM-dd` matcher — guards against malformed `c_deliveryWindows` entries. */
export const OHDS_ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Read a string-typed custom attribute off a `Shipment`. Returns `null` when
 * the shipment is missing, the attribute is unset, or the value is a
 * non-string / empty-after-trim. Defensive shape: SCAPI may serialise
 * unknown-shape `c_*` values as `unknown`, so we narrow at the boundary.
 */
export function getShipmentString(
    shipment: ShopperBasketsV2.schemas['Shipment'] | null | undefined,
    key: string
): string | null {
    if (!shipment) return null;
    const value = (shipment as unknown as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Normalise `c_deliveryWindows` off the delivery shipment. The backend
 * enrichment may serialise it as a JSON string or as an already-parsed
 * array, so accept both. Returned dates are filtered to valid `yyyy-MM-dd`
 * entries only — malformed entries are silently dropped (the upstream
 * `c_ohdsAvailable` flag is the authoritative empty-windows signal).
 *
 * NOTE: takes the RAW field value (not the shipment) so callers can
 * `useMemo` on the stable property reference rather than the (often fresh)
 * shipment wrapper.
 */
export function normaliseDeliveryWindows(raw: unknown): string[] {
    let candidate: unknown = raw;
    if (typeof raw === 'string') {
        try {
            candidate = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(candidate)) return [];
    return candidate.filter((value): value is string => typeof value === 'string' && OHDS_ISO_DATE_RE.test(value));
}

/**
 * Read a boolean-typed custom attribute off the basket (or any plain object).
 * Returns `false` for missing / non-true values — callers that need to
 * distinguish "explicitly false" from "unset" should read the raw value
 * themselves.
 */
export function getBasketBoolean(basket: unknown, key: string): boolean {
    if (!basket || typeof basket !== 'object') return false;
    return (basket as Record<string, unknown>)[key] === true;
}
